import React, { useState } from 'react';
import { Responsive as ResponsiveGridLayout, Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  useTheme,
  GlobalStyles,
  TextField,
  Stack,
  Divider,
  Alert,
  Skeleton,
} from '@mui/material';
import { Lock, Close } from '@mui/icons-material';
import WorkspaceAPI from '../../../../redux/workspace/WorkspaceAPI';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import PanelSelector from '../../../../theme/ui/Dashboard/PanelSelector/PanelSelector';
import { IAnyPanel } from '../../../../theme/ui/Dashboard/Dashboard.model';
import { EditIcon } from '../../../../assets';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useUserSettingState } from '../../../../redux/user/UserSettingsAPI/hook/useUserSettingState';

interface WorkspaceDashboardProps {
  workspaceId: string | null;
}

function WorkspaceDashboard(props: WorkspaceDashboardProps) {
  const { workspaceId } = props;
  const { data: workspace } = WorkspaceAPI.useGetWorkspaceByIdQuery(
    workspaceId ?? skipToken
  );
  const theme = useTheme();
  const [isEditMode, setIsEditMode] = useState(false);
  const [rowHeight, setRowHeight] = useState(90);
  const [xsColumns, setXsColumns] = useState(6);
  const [panels, setPanels] = useState<IAnyPanel[]>([
    {
      type: 'LABEL_VALUE',
      id: '1',
      title: 'Power consumption',
      value: '258 kW',
    },
    { type: 'LABEL_VALUE', id: '2', title: 'Temperature', value: '22.5 °C' },
    { type: 'LABEL_VALUE', id: '3', title: 'Active devices', value: '12' },
    { type: 'LABEL_VALUE', id: '4', title: 'Energy efficiency', value: '89%' },
    { type: 'TREND', id: '5', title: 'Uptime', subtitle: 'Last 7 days' },
    { type: 'TREND', id: '6', title: 'Downtime', subtitle: 'Last 7 days' },
    {
      type: 'PIE_CHART',
      id: '7',
      title: 'Device Status',
      subtitle: `Extracted: ${moment().format('LLL')}`,
      series: [
        {
          startAngle: -90,
          endAngle: 90,
          data: [
            { label: 'Healthy', value: 200 },
            { label: 'Disconnected', value: 10 },
            { label: 'Error', value: 30 },
          ],
          innerRadius: '150%',
          outerRadius: '200%',
          cy: '100%',
        },
      ],
    },
    {
      type: 'PIE_CHART',
      id: '8',
      title: 'Alarms',
      subtitle: `Extracted: ${moment().format('LLL')}`,
      series: [
        {
          data: [
            { label: 'Type 1', value: 72, color: theme.palette.error.main },
            { label: 'Type 2', value: 20, color: theme.palette.warning.main },
          ],
          innerRadius: '70%',
          outerRadius: '100%',
        },
      ],
    },
    {
      type: 'GAUGE',
      id: '9',
      title: 'System Performance',
      value: 60,
      subtitle: 'Overall efficiency',
    },
    {
      type: 'BAR_CHART',
      id: '10',
      title: 'Open Issues',
      subtitle: 'Issues opened daily',
      dataset: [
        { weekDay: 'Monday', value: 400 },
        { weekDay: 'Tuesday', value: 300 },
        { weekDay: 'Wednesday', value: 200 },
        { weekDay: 'Thursday', value: 500 },
        { weekDay: 'Friday', value: 400 },
        { weekDay: 'Saturday', value: 100 },
        { weekDay: 'Sunday', value: 50 },
      ],
      dataKey: 'value',
      xAxisKey: 'weekDay',
    },
    {
      type: 'DATA_GRID',
      id: '11',
      title: 'Alarms',
      subtitle: 'Recent system alarms',
      columns: [
        { field: 'date', headerName: 'Data', width: 150 },
        { field: 'title', headerName: 'Title', width: 200 },
        { field: 'column3', headerName: 'Column3', width: 150 },
        { field: 'column4', headerName: 'Column4', width: 150 },
      ],
      rows: [
        {
          id: 1,
          date: '2026-01-27',
          title: 'Temperature Alert',
          column3: 'High',
          column4: 'Active',
        },
        {
          id: 2,
          date: '2026-01-26',
          title: 'Power Failure',
          column3: 'Critical',
          column4: 'Resolved',
        },
        {
          id: 3,
          date: '2026-01-25',
          title: 'System Overload',
          column3: 'Medium',
          column4: 'Active',
        },
        {
          id: 4,
          date: '2026-01-24',
          title: 'Connection Loss',
          column3: 'Low',
          column4: 'Resolved',
        },
        {
          id: 5,
          date: '2026-01-23',
          title: 'Voltage Spike',
          column3: 'High',
          column4: 'Active',
        },
        {
          id: 6,
          date: '2026-01-22',
          title: 'Memory Warning',
          column3: 'Medium',
          column4: 'Active',
        },
        {
          id: 7,
          date: '2026-01-21',
          title: 'Network Timeout',
          column3: 'Low',
          column4: 'Resolved',
        },
        {
          id: 8,
          date: '2026-01-20',
          title: 'Disk Space Low',
          column3: 'Critical',
          column4: 'Active',
        },
        {
          id: 9,
          date: '2026-01-19',
          title: 'CPU Overheating',
          column3: 'High',
          column4: 'Resolved',
        },
        {
          id: 10,
          date: '2026-01-18',
          title: 'Fan Malfunction',
          column3: 'Medium',
          column4: 'Active',
        },
        {
          id: 11,
          date: '2026-01-17',
          title: 'Battery Backup Failed',
          column3: 'Critical',
          column4: 'Active',
        },
        {
          id: 12,
          date: '2026-01-16',
          title: 'Sensor Disconnected',
          column3: 'Low',
          column4: 'Resolved',
        },
        {
          id: 13,
          date: '2026-01-15',
          title: 'Pressure Anomaly',
          column3: 'High',
          column4: 'Active',
        },
        {
          id: 14,
          date: '2026-01-14',
          title: 'Communication Error',
          column3: 'Medium',
          column4: 'Resolved',
        },
        {
          id: 15,
          date: '2026-01-13',
          title: 'Firmware Update Required',
          column3: 'Low',
          column4: 'Active',
        },
      ],
    },
  ]);

  const defaultLayout: Layout = [
    { i: '1', x: 0, y: 0, w: 6, h: 1, moved: false, static: false },
    { i: '2', x: 3, y: 3, w: 3, h: 2, moved: false, static: false },
    { i: '3', x: 2, y: 3, w: 1, h: 1, moved: false, static: false },
    { i: '4', x: 0, y: 1, w: 2, h: 1, moved: false, static: false },
    { i: '5', x: 0, y: 7, w: 6, h: 3, moved: false, static: false },
    { i: '6', x: 0, y: 2, w: 2, h: 1, moved: false, static: false },
    { i: '7', x: 2, y: 1, w: 4, h: 2, moved: false, static: false },
    { i: '8', x: 0, y: 5, w: 3, h: 2, moved: false, static: false },
    { i: '9', x: 0, y: 3, w: 2, h: 2, moved: false, static: false },
    { i: '10', x: 3, y: 5, w: 3, h: 2, moved: false, static: false },
    { i: '11', x: 6, y: 2, w: 6, h: 5, moved: false, static: false },
  ];
  const {
    data: layout,
    setData: setLayout,
    isLoading: isLoadingLayout,
  } = useUserSettingState(
    `workspaceDashboardLayout:${workspaceId}`,
    defaultLayout
  );

  const handleLayoutChange = (newLayout: Layout) => {
    if (isEditMode) {
      setLayout(newLayout.map((item) => ({ ...item, static: !isEditMode })));
    }
  };

  React.useEffect(() => {
    if (layout)
      setLayout(
        layout.map((item) => ({ ...item, static: !isEditMode })),
        !isEditMode
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  // const handleAddPanel = () => {
  //   const newId = (panels.length + 1).toString();
  //   const newPanel: ILabelValuePanel = {
  //     id: newId,
  //     type: 'LABEL_VALUE',
  //     title: 'New metric',
  //     value: '0',
  //   };
  //   setPanels([...panels, newPanel]);

  //   const newLayoutItem: LayoutItem = {
  //     i: newId,
  //     x: (panels.length * 2) % 12,
  //     y: Infinity, // puts it at the bottom
  //     w: 2,
  //     h: 2,
  //     static: !isEditMode,
  //   };
  //   setLayout([...(layout ?? []), newLayoutItem]);
  // };

  const handleRemovePanel = (id: string) => {
    setPanels(panels.filter((p) => p.id !== id));
    layout && setLayout(layout.filter((l) => l.i !== id));
  };

  return (
    <Box sx={{ p: 3 }}>
      <GlobalStyles
        styles={{
          '.dashboard-layout .react-grid-item > .react-resizable-handle::after':
            {
              right: '5px',
              bottom: '5px',
              width: '10px',
              height: '10px',
              borderRight: `3px solid ${theme.palette.primary.main}`,
              borderBottom: `3px solid ${theme.palette.primary.main}`,
            },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4">{`${workspace?.name} Dashboard`}</Typography>
      </Box>
      <OverviewHeader
        isEditMode={isEditMode}
        onToggleEditMode={() => setIsEditMode(!isEditMode)}
        loading={isLoadingLayout}
      />
      {/* No logic for this yet */}
      {/* {isEditMode && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleAddPanel}
        >
          {t('dashboard.add_panel')}
        </Button>
      )} */}

      <Alert severity="info" sx={{ my: 2 }}>
        The information displayed on this dashboard is for demonstration
        purposes only. Is not connected to real data sources.
      </Alert>

      {isLoadingLayout && !layout ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={400}
          animation="wave"
        />
      ) : (
        <ResponsiveGridLayout
          className="dashboard-layout"
          layouts={{ lg: layout }}
          cols={{ lg: 12, md: xsColumns, sm: xsColumns, xs: xsColumns, xxs: 1 }}
          rowHeight={rowHeight}
          width={830}
          onLayoutChange={(currentLayout, allLayouts) =>
            handleLayoutChange(currentLayout)
          }
        >
          {panels.map((panel) => (
            <div key={panel.id}>
              <Paper
                elevation={3}
                sx={[
                  {
                    height: '100%',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    bgcolor: 'background.paper',
                  },
                  isEditMode
                    ? {
                        border: `2px dashed ${theme.palette.primary.main}`,
                      }
                    : {
                        border: 'none',
                      },
                ]}
              >
                {isEditMode && (
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                    onClick={() => handleRemovePanel(panel.id)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
                <PanelSelector panel={panel} />
              </Paper>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Test Controls
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            label="Row Height"
            type="number"
            value={rowHeight}
            onChange={(e) => setRowHeight(Number(e.target.value))}
            size="small"
            helperText="Height of each grid row in pixels"
          />

          <TextField
            label="XS Columns"
            type="number"
            value={xsColumns}
            onChange={(e) => setXsColumns(Number(e.target.value))}
            size="small"
            helperText="Number of columns on extra small screens spaces"
            inputProps={{ min: 1, max: 12 }}
          />
        </Box>
      </Box>
      <h2>Current Layout State:</h2>
      {JSON.stringify(layout)}
      <h2>Current Panels State:</h2>
      {JSON.stringify(panels)}
    </Box>
  );
}
interface OverviewHeaderProps {
  isEditMode: boolean;
  loading?: boolean;
  onToggleEditMode?: () => void;
}
function OverviewHeader({
  isEditMode,
  loading,
  onToggleEditMode,
}: OverviewHeaderProps) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Typography variant="h6" fontWeight={600}>
        {t('device.menu.overview')}
      </Typography>

      <Divider sx={{ flexGrow: 1 }} />

      <IconButton onClick={onToggleEditMode} loading={loading}>
        {isEditMode ? <Lock /> : <EditIcon />}
      </IconButton>
    </Stack>
  );
}

export default WorkspaceDashboard;
